<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-6 max-w-[1400px] mx-auto">

    <!-- Page header -->
    <div class="flex flex-row items-end justify-between gap-4 mb-5">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 tracking-tight">Suppliers</h1>
        <p class="text-sm text-gray-500 mt-1">Companies purchase orders are raised against</p>
      </div>
      <div class="flex items-center gap-3">
        <div class="hidden sm:flex items-center gap-4 bg-white border border-gray-200 rounded-full px-5 py-2 shadow-sm text-sm">
          <span><span class="font-semibold text-gray-800">{{ totals.suppliers }}</span> <span class="text-gray-400">suppliers</span></span>
          <span class="h-4 w-px bg-gray-200"></span>
          <span><span class="font-semibold text-gray-800">{{ totals.orders }}</span> <span class="text-gray-400">orders</span></span>
          <span class="h-4 w-px bg-gray-200"></span>
          <span><span class="font-semibold text-amber-700">{{ totals.open }}</span> <span class="text-gray-400">open</span></span>
        </div>
        <button
          class="group flex items-center gap-2.5 pl-2.5 pr-5 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-md shadow-amber-200 hover:shadow-lg hover:shadow-amber-300 hover:from-amber-300 hover:to-amber-400 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all focus:outline-none focus:ring-4 focus:ring-amber-200"
          (click)="onCreateSupplier()">
          <span class="h-8 w-8 rounded-lg bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform group-hover:rotate-90 duration-200">
            <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
          </span>
          New supplier
        </button>
      </div>
    </div>

    @if (loadError) {
      <div class="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between">
        <span>Could not load suppliers from the server.</span>
        <button class="text-xs uppercase font-semibold underline" (click)="load()">Try again</button>
      </div>
    }

    <!-- Search + sort -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-sm px-5 py-4 mb-5 flex flex-col sm:flex-row sm:items-center gap-3">
      <div class="relative flex-1">
        <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
        <input
          type="text"
          placeholder="Search by name, email, phone, city, country or VAT number"
          class="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
          [ngModel]="name"
          (ngModelChange)="onSearchChange($event)"
        />
        @if (name) {
        <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="onSearchChange('')" aria-label="Clear search">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
        </button>
        }
      </div>
      <button class="flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 hover:bg-white text-sm font-semibold text-gray-700 sm:w-56 transition-colors" [matMenuTriggerFor]="sortMenu">
        <span class="flex items-center gap-2"><mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">sort</mat-icon> {{ sortLabel }}</span>
        <mat-icon class="!text-[18px] !h-[18px] !w-[18px] !text-gray-400">expand_more</mat-icon>
      </button>
      <mat-menu #sortMenu="matMenu">
        @for (o of sortOptions; track o.key) {
        <button mat-menu-item (click)="setSort(o.key)"><span [class.font-semibold]="sort === o.key">{{ o.label }}</span></button>
        }
      </mat-menu>
    </div>

    @if (loading) {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-80 flex flex-col justify-center items-center">
        <mat-spinner diameter="32"></mat-spinner>
        <div class="text-sm text-gray-500 mt-3">Loading suppliers&hellip;</div>
      </div>
    } @else if (filtered.length === 0) {
      <div class="bg-white rounded-2xl border border-gray-100 shadow-sm min-h-80 flex flex-col justify-center items-center text-center">
        <span class="h-14 w-14 rounded-2xl bg-amber-50 flex items-center justify-center mb-3"><mat-icon class="!text-amber-500 !text-[30px] !h-[30px] !w-[30px]">storefront</mat-icon></span>
        <div class="text-lg font-semibold text-gray-800">{{ name ? 'No suppliers match your search' : 'No suppliers yet' }}</div>
        <div class="text-sm text-gray-500 mt-1">{{ name ? 'Try a different name, email or country' : 'Add the first company you buy from' }}</div>
        @if (!name) {
        <button class="mt-4 px-5 py-2.5 rounded-xl bg-amber-400 text-neutral-900 text-sm font-semibold hover:bg-amber-300" (click)="onCreateSupplier()">New supplier</button>
        }
      </div>
    } @else {

      <!-- Supplier cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        @for (s of page; track s.id) {
        <div class="group bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-amber-200 transition-all cursor-pointer flex flex-col" (click)="onEdit(s)">

          <div class="p-5 flex items-start gap-4">
            <div class="h-12 w-12 rounded-xl flex items-center justify-center text-base font-bold flex-shrink-0" [class]="avatarClass(s)">{{ initials(s) }}</div>
            <div class="min-w-0 flex-1">
              <div class="font-bold text-gray-900 leading-snug">{{ s.name }}</div>
              <div class="mt-1 flex flex-wrap items-center gap-1.5">
                @if (s.country) {
                <span class="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">public</mat-icon>{{ s.country }}</span>
                }
                @if (s.vatNumber) {
                <span class="text-[11px] font-mono px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-100">{{ s.vatNumber }}</span>
                }
              </div>
            </div>
            <button class="h-9 w-9 rounded-full bg-amber-50 text-amber-600 group-hover:bg-amber-400 group-hover:text-neutral-900 flex items-center justify-center transition-colors flex-shrink-0" (click)="$event.stopPropagation(); onEdit(s)" matTooltip="Edit supplier">
              <mat-icon class="!text-[18px]">edit_square</mat-icon>
            </button>
          </div>

          <!-- Contact -->
          <div class="px-5 pb-4 flex flex-col gap-1.5 text-sm">
            @if (s.email) {
            <a class="flex items-center gap-2 text-gray-700 hover:text-amber-700 min-w-0" [href]="'mailto:' + s.email" (click)="$event.stopPropagation()">
              <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">mail</mat-icon><span class="truncate">{{ s.email }}</span>
            </a>
            }
            @if (s.phone) {
            <a class="flex items-center gap-2 text-gray-700 hover:text-amber-700" [href]="'tel:' + s.phone" (click)="$event.stopPropagation()">
              <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">call</mat-icon>{{ s.phone }}
            </a>
            }
            @if (s.website) {
            <a class="flex items-center gap-2 text-gray-700 hover:text-amber-700 min-w-0" [href]="websiteUrl(s)" target="_blank" rel="noopener" (click)="$event.stopPropagation()">
              <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">language</mat-icon><span class="truncate">{{ s.website }}</span>
            </a>
            }
            @if (addressLine(s)) {
            <a class="flex items-start gap-2 text-gray-500 hover:text-amber-700" [href]="mapsUrl(s)" target="_blank" rel="noopener" (click)="$event.stopPropagation()" matTooltip="Open in Google Maps">
              <mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400 mt-0.5">place</mat-icon><span>{{ addressLine(s) }}</span>
            </a>
            }
            @if (!s.email && !s.phone && !s.website && !addressLine(s)) {
            <span class="text-gray-300 text-sm">No contact details yet</span>
            }
          </div>

          <!-- Business done -->
          <div class="mt-auto px-5 py-3 border-t border-gray-100 bg-gray-50/60 rounded-b-2xl flex items-center justify-between text-xs">
            @if (s.orderCount) {
            <span class="flex items-center gap-1.5 text-gray-600 font-semibold"><mat-icon class="!text-[16px] !h-4 !w-4 !text-gray-400">receipt_long</mat-icon>{{ s.orderCount }} order{{ s.orderCount === 1 ? '' : 's' }}</span>
            <span class="flex items-center gap-2">
              @if (s.openOrders) { <span class="font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{{ s.openOrders }} open</span> }
              <span class="text-gray-400">last {{ s.lastOrderDate | date:'d MMM yyyy' }}</span>
            </span>
            } @else {
            <span class="text-gray-400">No orders raised yet</span>
            }
          </div>
        </div>
        }
      </div>

      <mat-paginator class="mt-4 rounded-2xl border border-gray-100 shadow-sm"
        [length]="filtered.length" [pageSize]="pageSize" [pageIndex]="pageIndex"
        [pageSizeOptions]="[12, 24, 48]" showFirstLastButtons (page)="onPage($event)">
      </mat-paginator>
    }

  </div>
  </div>
</app-main-layout>
